import React, { useEffect, useState } from "react";
import { Button, Input, Modal, } from "reactstrap";
import { useDispatch } from "react-redux";
import { showToaster } from "../../../../constant";
import CustomeButton from "../../Forms/CustomeButton/CustomeButton";
import DropDown from "../../Forms/DropDown/DropDown";
import { stateGet, useStates } from "../../../../redux/slices/authSlice";
import { AddStatePayInMeta, GetAvailableStatePaymentMeta, useAvailableStatePaymentMeta, GetStatePayInMeta } from "../../../../redux/slices/statePayInMetaSlice";

const staticLevels = [
    { value: "1", label: "Level 1" },
    { value: "2", label: "Level 2" },
    { value: "3", label: "Level 3" },
    { value: "4", label: "Level 4" },
];

const upiAppsOptions = [
    { value: "is_phonepe", label: "PhonePe" },
    { value: "is_paytm", label: "Paytm" },
    { value: "is_googlepay", label: "GooglePay" },
    { value: "all", label: "All" },
];

const initialOthers = {
    is_phonepe: 0,
    is_paytm: 0,
    is_googlepay: 0,
    all: 0,
};

const AddStateBankModal = ({ isOpen, onHide }) => {
    const dispatch = useDispatch();
    const states = useStates();
    const availableBanksData = useAvailableStatePaymentMeta();

    const [selectedState, setSelectedState] = useState("");
    const [selectedBank, setSelectedBank] = useState(null);
    const [methods, setMethods] = useState([]);
    const [levels, setLevels] = useState([]);
    const [others, setOthers] = useState(initialOthers);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            dispatch(stateGet());
        } else {
            // Reset state when closing
            setSelectedState("");
            setSelectedBank(null);
            setMethods([]);
            setLevels([]);
            setOthers(initialOthers);
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedState) {
            dispatch(GetAvailableStatePaymentMeta({ stateId: selectedState }));
            setSelectedBank(null);
            setMethods([]);
        }
    }, [selectedState]);

    const bankOptions = [
        ...(availableBanksData?.data?.auto || []),
        ...(availableBanksData?.data?.manual || [])
    ].map(bank => ({
        label: `${bank.pgName} (${bank.pgLabel})`,
        value: bank.accountId,
        data: bank
    }));

    const stateOptions = (states?.data || []).map(s => ({
        label: s.name,
        value: s.state_uid
    }));

    const handleBankChange = (val) => {
        const option = bankOptions.find(o => o.value === val.value);
        setSelectedBank(option?.data);
        setMethods([]); // Reset methods when bank changes
    };

    const handleMethodChange = (method) => {
        if (methods.includes(method)) {
            setMethods(methods.filter(m => m !== method));
        } else {
            setMethods([...methods, method]);
        }
    };

    const handleLevelChange = (level) => {
        if (levels.includes(level)) {
            setLevels(levels.filter(l => l !== level));
        } else {
            setLevels([...levels, level]);
        }
    };

    const prepareChecked = (value) => {
        if (value === "all") {
            const values = Object.entries(others || {})
                .filter(([key]) => key !== "all")
                .map(([_, item]) => item);
            return !values?.includes(0);
        } else {
            return !!others?.[value];
        }
    };

    const handleOtherChange = (val) => {
        if (val === "all") {
            const newValue = others.all === 0 ? 1 : 0;
            setOthers({
                is_phonepe: newValue,
                is_paytm: newValue,
                is_googlepay: newValue,
                all: newValue,
            });
        } else {
            setOthers({
                ...others,
                [val]: others?.[val] === 0 ? 1 : 0,
            });
        }
    };

    // Update 'all' checkbox based on other upi apps to maintain consistency with preparation helper
    useEffect(() => {
        const { is_phonepe, is_paytm, is_googlepay } = others;
        if (is_phonepe === 1 && is_paytm === 1 && is_googlepay === 1) {
            if (others.all !== 1) setOthers(prev => ({ ...prev, all: 1 }));
        } else {
            if (others.all !== 0) setOthers(prev => ({ ...prev, all: 0 }));
        }
    }, [others.is_phonepe, others.is_paytm, others.is_googlepay]);

    const handleSubmit = async () => {
        if (!selectedState || !selectedBank || !methods.length || !levels.length) {
            showToaster("Please fill all required fields", "Error");
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                pgName: selectedBank.pgName,
                stateId: selectedState,
                pgId: selectedBank.accountId,
                paymentMethod: methods,
                level: levels,
                is_phonepe: others.is_phonepe,
                is_paytm: others.is_paytm,
                is_googlepay: others.is_googlepay,
            };
            const res = await dispatch(AddStatePayInMeta(payload)).unwrap();
            showToaster(res?.message || "Bank added successfully", "Success");
            setIsSubmitting(false);
            dispatch(GetStatePayInMeta());
            onHide();
        } catch (err) {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={onHide} centered={true} size="lg">
            <div className="modal-header">
                <h3 className="modal-title mt-0">Add State Bank</h3>
                <i className="fa-solid fa-xmark cursor-pointer" onClick={onHide}></i>
            </div>
            <div className="modal-body">
                <div className="row">
                    <div className="col-6">
                        <DropDown
                            label="Select State"
                            options={stateOptions}
                            value={selectedState}
                            onChange={(val) => setSelectedState(val.value)}
                        />
                    </div>
                    <div className="col-6">
                        <DropDown
                            label="Select Bank"
                            options={bankOptions}
                            value={selectedBank?.accountId}
                            onChange={handleBankChange}
                            isDisabled={!selectedState}
                            placeholder={!selectedState ? "Please select a state first" : "Select a bank"}
                        />
                    </div>
                </div>


                {selectedBank ? (
                    <>
                        <div className="mt-1 text-center">
                            <p className="mb-0">
                                {`${selectedBank?.pgName}(${selectedBank?.accountId}) select method and level`}
                            </p>
                        </div>
                        <div className="d-flex justify-content-center">
                            <div className="p-0 gap-1 d-flex flex-wrap align-items-center my-1">
                                {selectedBank.meta_methods?.map((method) => (
                                    <div key={method} className="gap">
                                        <Input
                                            type="checkbox"
                                            checked={methods.includes(method)}
                                            onChange={() => handleMethodChange(method)}
                                        />
                                        {method}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="level-main">
                            <div className="p-1 levels">
                                <h5>Levels</h5>
                                {staticLevels.map((lvl) => (
                                    <div key={lvl.value} className="d-flex align-items-center gap mb-50">
                                        <Input
                                            type="checkbox"
                                            checked={levels.includes(lvl.value)}
                                            onChange={() => handleLevelChange(lvl.value)}
                                        />
                                        {lvl.label}
                                    </div>
                                ))}
                            </div>
                            <div className="p-1">
                                <h5>UPI Apps</h5>
                                {upiAppsOptions.map((app) => (
                                    <div key={app.value} className="d-flex align-items-center gap mb-50">
                                        <Input
                                            type="checkbox"
                                            checked={prepareChecked(app.value)}
                                            onChange={() => handleOtherChange(app.value)}
                                        />
                                        {app.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ height: '300px' }} className="d-flex align-items-center justify-content-center">
                        <p className="text-muted">Please select a state and bank to configure methods and levels.</p>
                    </div>
                )}
            </div>
            <div className="modal-footer">
                <div className="d-flex justify-content-end button-space">
                    <Button className="graybutton" onClick={onHide}>
                        Cancel
                    </Button>
                    <CustomeButton
                        className="submitbutton"
                        isButtonLoading={isSubmitting}
                        onClick={handleSubmit}
                    >
                        Submit
                    </CustomeButton>
                </div>
            </div>
        </Modal>
    );
};

export default AddStateBankModal;
